import { DatadogDurationVitalStopper } from '@/app-shell/app-root/datadogDurationVitalStopper';
import { useQueryInterruptedSessions } from '@/app-shell/chronicle/hooks/query/useQueryInterruptedSessions';
import { DEFAULT_DATE_FORMAT } from '@/app-shell/constants';
import { DatadogDurationVital } from '@/app-shell/constants/datadog-vitals';
import { logCopilotAction } from '@/app-shell/copilot/action-log';
import {
  COPILOT_PRECHART_REQUEST_TTL_MS,
  copilotPrechartBatchAtom,
  copilotPrechartRequestAtom,
} from '@/app-shell/copilot/prechart-atoms';
import { useIsLiveRunningRemoteSession } from '@/app-shell/desktop/hooks/useIsLiveRunningRemoteSession';
import { useIsPureOfflineRemoteSession } from '@/app-shell/desktop/hooks/useIsPureOfflineRemoteSession';
import { useIsAmbientCopilotEnabled } from '@/app-shell/flags/agents-flags';
import { IS_HIDE_EMAIL_FUNCTIONALITY_ENABLED } from '@/app-shell/flags/auth-flags';
import { IS_LIVE_TRANSCRIPTION_INDICATOR_ENABLED } from '@/app-shell/flags/capture-flags';
import {
  useIsDesktopChronicleEnabled,
  useIsOfflineRecordingEnabled,
  useIsReleaseRemoteSyncAsyncWebEnabled,
} from '@/app-shell/flags/desktop-flags';
import {
  GET_EVIDENCE_AWARENESS_POPUP_VARIANT,
  IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED,
} from '@/app-shell/flags/evidence-flags';
import {
  IS_CONSOLIDATED_SESSIONS_LIST_ENABLED,
  IS_UPCOMING_SESSIONS_ENABLED,
  useIsForActionSessionStatusEnabled,
  useIsNewSessionListEnabled,
  useIsSessionPageV2Enabled,
  useIsSessionUrgencyEnabled,
} from '@/app-shell/flags/session-flags';
import {
  IS_ASSISTANT_VIEW_ENABLED,
  IS_SIDEBAR_PANEL_ENABLED,
} from '@/app-shell/flags/shell-flags';
import { useVisibleSidebarPanelContent } from '@/app-shell/layout/Navigation/sidebar-panel/hooks/use-visible-sidebar-panel-content';
import { getSidebarPanelOverlayTriggerProps } from '@/app-shell/layout/Navigation/sidebar-panel/sidebar-overlay-constants';
import { isShortcutsDisabledAtom } from '@/app-shell/layout/ShortcutsHelper/shortcutsAtom';
import { canCollapseAtom } from '@/app-shell/layout/header/mobile-app-header/mobile-app-header.atoms';
import { isMobileHeaderSheetOpenAtom } from '@/app-shell/layout/header/mobile-app-header/mobile-header-sheet-atoms';
import { OfflineGatedTooltip } from '@/app-shell/offline-recording/offline-gated-tooltip';
import { offlineGatingMessages } from '@/app-shell/offline-recording/offline-gating-messages';
import { useOfflineAwareSyncStatus } from '@/app-shell/offline-recording/sync-status-badge/hooks/useOfflineAwareSyncStatus';
import { SyncStatusBadge } from '@/app-shell/offline-recording/sync-status-badge/sync-status-badge';
import { useShowSessionReview } from '@/app-shell/session-page/hooks/useShowSessionReview';
import { useTeamClinicianList } from '@/app-shell/session-page/hooks/useTeamClinicianList';
import { useTidyUpRecordingLock } from '@/app-shell/session-page/hooks/useTidyUpRecordingLock';
import { useSettingsModal } from '@/app-shell/settings/hooks/useSettingsModal';
import { useIntegrationsMarketplaceVisibilityGate } from '@/app-shell/settings/integrations/integrations-marketplace-visibility-gate';
import { TrackedButton } from '@/app-shell/ui/tracked-button';
import { ProfileSearchSvg } from '@/assets/icons/profile-search-svg';
import { RemoteDeviceSvg } from '@/assets/icons/remote-device-svg';
import { HeidiTogetherBoxesSvg } from '@/assets/image/free-to-pay/heidi-together-boxes-svg';
import {
  bulkSyncCumulativeProcessedAtom,
  bulkSyncExpectedUploadTotalAtom,
  bulkSyncPhaseAtom,
  bulkSyncUploadProgressAtom,
  chronicleDeviceRecordingInfoAtom,
  chronicleIsPausedAtom,
  chronicleLiveOfflineAtom,
  chronicleReconnectedPendingEndAtom,
  chronicleSessionIdAtom,
} from '@/atoms/chronicle';
import {
  offlineActiveRecordingIdAtom,
  offlineCaptureRunningAtom,
  offlineDismissedSyncedBadgeIdsAtom,
  offlineLocalSessionsAtom,
  offlineNoteFailedDismissedSessionIdsAtom,
  offlineNoteFailedSessionIdsAtom,
  offlineSyncQueueAtom,
  useOfflinePendingServerDeleteIds,
} from '@/atoms/offline-recording';
import { offlinePatientLinkAtomFamily } from '@/atoms/offline-recording/patient-link';
import { evidenceSidebarOpenAtom } from '@/atoms/panelAtoms';
import { usePaywallStates } from '@/atoms/paywallAtoms';
import {
  setSidebarOpenTab,
  sidebarOpenTabAtom,
  SidebarTab,
} from '@/atoms/persistedPreferencesAtoms';
import {
  sessionlessEvidenceAwarenessDevForceShowAtom,
  sessionlessEvidenceAwarenessDevVariantOverrideAtom,
} from '@/atoms/sessionlessEvidenceAwarenessDevAtoms';
import { sidebarPanelContentAtom } from '@/atoms/sidebarPanelAtoms';
import styles from '@/components/tiptap/shared/styles/index.module.scss';
import { useBlockEditorMarkdown } from '@/components/tiptap/vMarkdown/useBlockEditor';
import {
  ability,
  ACTIONS,
  hasUpdatedAbilityAtom,
  PERMISSION_SUBJECTS,
} from '@/helpers/casl/ability';
import { simpleMarkdownToHtml } from '@/helpers/markdownToHtml';
import { filterAndSortSessions } from '@/helpers/sessionSortHelpers';
import { useIsInterruptedRemoteSession } from '@/hooks/desktop/useIsInterruptedRemoteSession';
import { useIsLiveOfflineRemoteSession } from '@/hooks/desktop/useIsLiveOfflineRemoteSession';
import { useIsRemoteReconnectedSession } from '@/hooks/desktop/useIsRemoteReconnectedSession';
import { useOfflineGating } from '@/hooks/desktop/useOfflineGating';
import { useQueryIntegrationsMixinGetSchedule } from '@/hooks/integrations/integrations-framework/mixins/useQueryIntegrationsMixinGetSchedule';
import { useEhrLaunchSessionId } from '@/hooks/integrations/useEhrLaunchSessionId';
import { useHasActiveEhrIntegration } from '@/hooks/integrations/useHasActiveEhrIntegration';
import { useIntegrationEhrInfo } from '@/hooks/integrations/useIntegrationEhrInfo';
import { useIsEhrLaunch } from '@/hooks/integrations/useIsEhrLaunch';
import { useMutateStatePostIntegrationWorkflowSession } from '@/hooks/mutation/integration/direct-backend/useMutatePostIntegrationWorkflowSession';
import { useMutateUpdateSessionEHRAppointmentId } from '@/hooks/mutation/integration/useMutateUpdateSessionEHRAppointmentId';
import { useMutateCreateNewSession } from '@/hooks/mutation/useMutateCreateNewSession';
import { useMutateCreateAndLinkPatientProfile } from '@/hooks/mutation/useMutateCreatePatientProfile';
import { useMutateLinkSessionsToPatientProfile } from '@/hooks/mutation/useMutateLinkSessionsToPatientProfile';
import { useMutateUpdateSyncDocumentById } from '@/hooks/mutation/useMutateUpdateDocumentById';
import { useMutateUpdateSessionStatus } from '@/hooks/mutation/useMutateUpdateSessionStatus';
import { useQueryEHRAppointments } from '@/hooks/query/integrations/useQueryEHRAppointments';
import { useQueryUserEHR } from '@/hooks/query/integrations/useQueryUserEHR';
import { useCombinedOrganizationInfo } from '@/hooks/query/useCombinedOrganizationInfo';
import { useQueryCombinedSessions } from '@/hooks/query/useQueryCombinedSessions';
import { useQueryPatientProfiles } from '@/hooks/query/useQueryPatientProfiles';
import { usePrefetchSessionById } from '@/hooks/query/useQuerySessionById';
import { useGetPageSize } from '@/hooks/query/useQuerySessionsV2';
import useQueryUserTier from '@/hooks/query/useQueryUserTier';
import { useAuth } from '@/hooks/useAuth';
import { useDebouncedNewSession } from '@/hooks/useDebouncedNewSession';
import { useIsDirectSessionSharingAllowed } from '@/hooks/useIsDirectSessionSharingAllowed';
import { useNewSession, useNewSessionMutationPending } from '@/hooks/useNewSession';
import { useRBACPermissionCheck } from '@/hooks/useRBACPermissionCheck';
import { useSessionCopy } from '@/hooks/useSessionCopy';
import { useSessionDateDisplay } from '@/hooks/useSessionDateDisplay';
import { useSessionListSpotlightActions } from '@/hooks/useSessionListSpotlightTour';
import { useSessionsListHotkeys } from '@/hooks/useSessionsListHotkeys';
import {
  useNewSessionDelegation,
  useShowClinicianSelector,
} from '@/hooks/useShowClinicianSelector';
import { useTutorial } from '@/hooks/useTutorial';
import { trackPatientSelected } from '@/lib/analytics/scribe/patient';
import { hotkeysList } from '@/lists/hotkeys';
import { useSessionSearchTracking } from '@/models/analytics/features/useSessionSearchTracking';
import { isOfflineDeletableStatus } from '@/tauri/offline-recording/offline-recording';
import { isOnPrem } from '@/utils/buildProfile';
import { enumToDescription } from '@/utils/enumToDescription';
import { getEhrApptTime, TIME_FORMAT } from '@/utils/integration/parse-ehr-appt-time';
import { printDocument } from '@/utils/printHelpers';
import { datadogRum } from '@datadog/browser-rum';
import { useSessionRecordingStore } from '@heidi/capture';
import { useUserTier } from '@heidi/commerce/data';
import { EhrIcon } from '@heidi/integrations/ehr';
import { IntegrationLaunchSource } from '@heidi/integrations/ehr/contracts';
import {
  hasIntegrationAtom,
  integrationCallbacksAtom,
} from '@heidi/integrations/ehr/data/atoms';
import {
  useIsCalendarEventTitleEnabled,
  useIsProblemBasedChartingEnabled,
  useIsScheduleDateRangeFilterEnabled,
  useIsScheduleFacetFilteringEnabled,
  useIsScheduleRowFacetsEnabled,
} from '@heidi/integrations/flags';
import {
  buildSessionStatusOptions,
  compareSessionsByStatus,
  deleteSessionDataAtom,
  isSessionUrgent,
  useVisibleSessionStatuses as selectVisibleSessionStatuses,
  SESSION_STATUS_DISPLAY_ORDER,
  type SessionListHost,
  SessionMergeIcon,
  sessionStatusChipConfig,
  SessionUrgencyFlag as SessionUrgencyFlagCore,
  sessionUrgencyMessages,
  showSendAsEmailModal,
  statusChipVariants,
  statusIconColors,
  statusIconVariants,
  useFormatSessionTime,
  useMutateDeleteEmptySessions,
} from '@heidi/session';
import { useCanViewOthersSessions } from '@heidi/workspace';

import {
  activeSessionIdAtom,
  inFlightRecordingSessionIdAtom,
  pendingRecordingActionAtom,
  scheduleDateAtom,
  sessionListAnchorsMountedAtom,
  sessionListExpandAnchorAvailableAtom,
  sessionListSpotlightStepAtom,
  sessionQueryVersionAtom,
} from '@heidi/session/data';
import { exportFontModalAtom } from '@heidi/session/state';
import { Loader } from '@heidi/snowflake-ui/loader';
import {
  MobileDrawerHeader,
  MobileFilterRow,
} from '@heidi/snowflake-ui/mobile-filter-drawer';
import { parseTimeStamp, useMediaQuery } from '@heidi/utils';
import { selectedReviewMemberAtom } from '@heidi/workspace/data';
import { getDefaultStore, useAtom, useAtomValue, useSetAtom } from 'jotai';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { type ComponentType, createElement } from 'react';

import { useSessionListVersion } from './useSessionListVersion';
import { useShowSessionsListAssistantView } from './useShowSessionsListAssistantView';

// Only the list table renders these two, but every route that mounts the sidebar
// imports this host. Static imports would put their patient-linking cone in
// those routes' first load, so they load on first render instead.
export const loadPatientLinkAvatar = () =>
  import('./patient-link-avatar').then((mod) => ({ default: mod.PatientLinkAvatar }));

export const loadSessionListLinkOverlays = () =>
  import('./session-list-link-overlays').then((mod) => ({
    default: mod.SessionListLinkOverlays,
  }));

const rethrowLoadError = ({ error }: { error?: Error | null }) => {
  if (error) throw error;
  return null;
};

// Holds the `sm` avatar's footprint (24px, no shrink) so the patient name does
// not shift right when the avatar arrives.
export const loadConsolidatedSessionLinkAvatar = () =>
  import('./consolidatedSessionLinkAvatar').then((mod) => ({
    default: mod.ConsolidatedSessionLinkAvatar,
  }));

export const loadSessionsListFooterLinkControl = () =>
  import('./sessions-list-footer-link-control').then((mod) => ({
    default: mod.SessionsListFooterLinkControl,
  }));

export const loadAccordionNavV3Button = () =>
  import('@/app-shell/layout/link/accordion-nav-v3-button');

export const loadUploadSessionScheduleModal = () =>
  import('@/app-shell/layout/lazy-session-dialogs').then((mod) => ({
    default: mod.UploadSessionScheduleModal,
  }));

export const loadSessionlessEvidenceAwarenessPopover = () =>
  import('@/components/modal/sessionless-evidence-awareness-popover').then((mod) => ({
    default: (props: { variant: 'sidebar' }) =>
      createElement(
        mod.SessionlessEvidenceAwarenessPopover as ComponentType<{ variant: 'sidebar' }>,
        props
      ),
  }));

export const loadEhrRePairBanner = () =>
  import('@heidi/integrations/ehr').then((mod) => ({
    default: mod.EhrRePairBanner,
  }));

const PatientLinkAvatarPlaceholder = ({ error }: { error?: Error | null }) => {
  if (error) throw error;
  return createElement('span', {
    'aria-hidden': true,
    className: 'block size-6 shrink-0',
    'data-testid': 'session-list-patient-avatar-placeholder',
  });
};

const PatientLinkAvatar = dynamic(loadPatientLinkAvatar, {
  ssr: false,
  loading: PatientLinkAvatarPlaceholder,
});

const SessionListLinkOverlays = dynamic(loadSessionListLinkOverlays, {
  ssr: false,
  loading: rethrowLoadError,
});

const ConsolidatedSessionLinkAvatar = dynamic(loadConsolidatedSessionLinkAvatar, {
  ssr: false,
  loading: PatientLinkAvatarPlaceholder,
});

const SessionsListFooterLinkControl = dynamic(loadSessionsListFooterLinkControl, {
  ssr: false,
  loading: rethrowLoadError,
});

// Holds the v1 row's 46px footprint so `VList` rows don't collapse to 0px while the chunk loads.
export const accordionNavV3ButtonFallback = ({ error }: { error?: Error | null }) => {
  if (error) throw error;
  return createElement('div', { className: 'h-[46px] w-full', 'aria-hidden': true });
};

const AccordionNavV3Button = dynamic(loadAccordionNavV3Button, {
  ssr: false,
  loading: accordionNavV3ButtonFallback,
});

const UploadSessionScheduleModal = dynamic(loadUploadSessionScheduleModal, {
  ssr: false,
});

const LazySessionlessEvidenceAwarenessPopover = dynamic(
  loadSessionlessEvidenceAwarenessPopover,
  {
    ssr: false,
  }
);

const SessionlessEvidenceAwarenessPopover = (props: { variant: 'sidebar' }) =>
  createElement(LazySessionlessEvidenceAwarenessPopover, props);

const EhrRePairBanner = dynamic(loadEhrRePairBanner, { ssr: false });

// Action-center slots. Static imports would put the modal and the export
// submenu in every route that mounts the sidebar, including haiku.
export const loadSendAsEmailModal = () =>
  import('@/components/ui/more-dropdown-menu/send-as-email').then((mod) => ({
    default: mod.default,
  }));

export const loadExportSubmenu = () =>
  import('@/composition/hosts/session/export-files/export-format-items/export-format-items').then(
    (mod) => ({
      default: mod.ExportSubmenu,
    })
  );

const SendAsEmailModal = dynamic(loadSendAsEmailModal, {
  ssr: false,
  loading: rethrowLoadError,
});

const ExportSubmenu = dynamic(loadExportSubmenu, {
  ssr: false,
  loading: rethrowLoadError,
});

const useShowSendAsEmailModal = () => useAtomValue(showSendAsEmailModal);

const useSetShowSendAsEmailModal = () => useSetAtom(showSendAsEmailModal);

const useExportFontRequest = () => useAtomValue(exportFontModalAtom);

const useSetExportFontRequest = () => useSetAtom(exportFontModalAtom);

/** The CASL ability has been written for this user. */
export function useHasUpdatedAbility(): boolean {
  return useAtomValue(hasUpdatedAbilityAtom);
}

/** The copilot pre-chart request, read inside React. */
const useCopilotPrechartRequest = () => useAtomValue(copilotPrechartRequestAtom);

/** The setter for the batch the orchestrator drains. */
const useSetCopilotPrechartBatch = () => useSetAtom(copilotPrechartBatchAtom);

/** Which sessions query the legacy filter dropdown is driving. */
export const useSessionQueryVersion = () => useAtomValue(sessionQueryVersionAtom);

/** Pins the legacy dropdown onto the v1 query while it is open. */
export const useSetSessionQueryVersion = () => useSetAtom(sessionQueryVersionAtom);

/** Review-member picker. The atom stays app-side. */
export const useSetSelectedReviewMember = () => useSetAtom(selectedReviewMemberAtom);

/** Keyboard shortcuts are suppressed (a modal or field owns the keys). */
export const useIsShortcutsDisabled = () => useAtomValue(isShortcutsDisabledAtom);

/** Spotlight tour step, or null when the tour is closed. */
export const useSessionListSpotlightStep = () =>
  useAtomValue(sessionListSpotlightStepAtom);

/** Whether step 4's expand control is mounted. */
export const useSessionListExpandAnchorAvailable = () =>
  useAtomValue(sessionListExpandAnchorAvailableAtom);

/** Writes the expand-anchor flag from the header that mounts it. */
export const useSetSessionListExpandAnchorAvailable = () =>
  useSetAtom(sessionListExpandAnchorAvailableAtom);

/** Opens the delete-session modal. The atom stays app-side. */
export const useSetDeleteSessionData = () => useSetAtom(deleteSessionDataAtom);

export const useOfflineActiveRecordingId = () =>
  useAtomValue(offlineActiveRecordingIdAtom);

/** Evidence sidebar open flag. The atom stays app-side. */
export const useSetEvidenceSidebarOpen = () => useSetAtom(evidenceSidebarOpenAtom);

export const useChronicleDeviceRecordingInfo = () =>
  useAtomValue(chronicleDeviceRecordingInfoAtom);

export const useChronicleIsPaused = () => useAtomValue(chronicleIsPausedAtom);

export const useChronicleLiveOffline = () => useAtomValue(chronicleLiveOfflineAtom);

export const useChronicleReconnectedPendingEnd = () =>
  useAtomValue(chronicleReconnectedPendingEndAtom);

export const useChronicleSessionId = () => useAtomValue(chronicleSessionIdAtom);

export const useBulkSyncPhase = () => useAtomValue(bulkSyncPhaseAtom);

export const useBulkSyncUploadProgress = () => useAtomValue(bulkSyncUploadProgressAtom);

export const useBulkSyncExpectedUploadTotal = () =>
  useAtomValue(bulkSyncExpectedUploadTotalAtom);

export const useBulkSyncCumulativeProcessed = () =>
  useAtomValue(bulkSyncCumulativeProcessedAtom);

export const useActiveSessionId = () => useAtomValue(activeSessionIdAtom);

export const useInFlightRecordingSessionId = () =>
  useAtomValue(inFlightRecordingSessionIdAtom);

export const useSetPendingRecordingAction = () => useSetAtom(pendingRecordingActionAtom);

export const useOfflineCaptureRunning = () => useAtomValue(offlineCaptureRunningAtom);

export const useOfflineLocalSessions = () => useAtomValue(offlineLocalSessionsAtom);

export const useOfflineSyncQueue = () => useAtomValue(offlineSyncQueueAtom);

export const useOfflineDismissedSyncedBadgeIds = () =>
  useAtomValue(offlineDismissedSyncedBadgeIdsAtom);

export const useOfflineNoteFailedDismissedSessionIds = () =>
  useAtomValue(offlineNoteFailedDismissedSessionIdsAtom);

export const useOfflineNoteFailedSessionIds = () =>
  useAtomValue(offlineNoteFailedSessionIdsAtom);

/** Typed patient id for one offline session. Empty string means none. */
export const useOfflinePatientLink = (sessionId: string) =>
  useAtomValue(offlinePatientLinkAtomFamily(sessionId));

const useCanCollapse = () => useAtomValue(canCollapseAtom);

const useSetIsMobileHeaderSheetOpen = () => useSetAtom(isMobileHeaderSheetOpenAtom);

const useSidebarOpenTab = () => useAtomValue(sidebarOpenTabAtom);

const useSetSidebarPanelContent = () => useSetAtom(sidebarPanelContentAtom);

const useHasIntegration = () => useAtomValue(hasIntegrationAtom);

const useIntegrationCallbacks = () => useAtomValue(integrationCallbacksAtom);

const useSessionlessEvidenceAwarenessDevVariantOverride = () =>
  useAtomValue(sessionlessEvidenceAwarenessDevVariantOverrideAtom);

const useSessionlessEvidenceAwarenessDevForceShow = () =>
  useAtomValue(sessionlessEvidenceAwarenessDevForceShowAtom);

const useSessionRecordingStatus = () => useSessionRecordingStore((s) => s.status);

const useSelectedReviewMember = () => useAtomValue(selectedReviewMemberAtom);

const useScheduleDate = () => useAtomValue(scheduleDateAtom);

const useScheduleDateState = () => useAtom(scheduleDateAtom);

// The appointment list only mounts with the new session list; the v1 schedule
// calendar keeps driving its own single day.
const useIsScheduleDateRangeEnabled = () => {
  const isDateRangeFilterEnabled = useIsScheduleDateRangeFilterEnabled();
  const isNewSessionListEnabled = useIsNewSessionListEnabled();
  return isDateRangeFilterEnabled && isNewSessionListEnabled;
};

const useSetSessionListAnchorsMounted = () => useSetAtom(sessionListAnchorsMountedAtom);

const startDurationVital = (name: string) => datadogRum.startDurationVital(name);

/**
 * One object, created once. Callers pass this reference; it is not rebuilt
 * per render, so it stays out of the first-load graph.
 */
export function useVisibleSessionStatuses() {
  return selectVisibleSessionStatuses(useIsForActionSessionStatusEnabled());
}

export const SessionUrgencyFlag: SessionListHost['sessionApp']['SessionUrgencyFlag'] = (
  props
) =>
  createElement(SessionUrgencyFlagCore, {
    ...props,
    isSessionUrgencyEnabled: useIsSessionUrgencyEnabled(),
  });

export const sessionListHost: SessionListHost = {
  identity: {
    ability,
    ACTIONS,
    PERMISSION_SUBJECTS,
    useRBACPermissionCheck,
    useShowClinicianSelector,
    useTeamClinicianList,
    useQueryUserTier,
    useUserTier,
    useAuth,
    useNewSessionDelegation,
    useSetSelectedReviewMember,
    useHasUpdatedAbility,
    useCanViewOthersSessions,
    useIsDirectSessionSharingAllowed,
    useCombinedOrganizationInfo,
    useShowSessionReview,
    usePaywallStates,
    useSelectedReviewMember,
  },
  integrations: {
    useHasActiveEhrIntegration,
    useIntegrationsMarketplaceVisibilityGate,
    useIsEhrLaunch,
    useEhrLaunchSessionId,
    useHasIntegration,
    useIntegrationCallbacks,
    IntegrationLaunchSource,
    useIntegrationEhrInfo,
    useQueryIntegrationsMixinGetSchedule,
    useQueryEHRAppointments,
    useQueryUserEHR,
    useMutateStatePostIntegrationWorkflowSession,
    useMutateUpdateSessionEHRAppointmentId,
    TIME_FORMAT,
    getEhrApptTime,
    EhrRePairBanner,
    EhrIcon,
  },
  shell: {
    isOnPrem,
    useIsNewSessionListEnabled,
    useIsSessionUrgencyEnabled,
    IS_SIDEBAR_PANEL_ENABLED,
    IS_CONSOLIDATED_SESSIONS_LIST_ENABLED,
    getSidebarPanelOverlayTriggerProps,
    Loader,
    MobileDrawerHeader,
    MobileFilterRow,
    parseTimeStamp,
    useSettingsModal,
    useRouter,
    reportError: datadogRum.addError,
    useIsSessionPageV2Enabled,
    IS_LIVE_TRANSCRIPTION_INDICATOR_ENABLED,
    hotkeysList,
    useIsShortcutsDisabled,
    TrackedButton,
    SessionMergeIcon,
    useMediaQuery,
    useSessionListSpotlightStep,
    useSessionListExpandAnchorAvailable,
    useSetSessionListExpandAnchorAvailable,
    useSessionListSpotlightActions,
    SidebarTab,
    useSidebarOpenTab,
    setSidebarOpenTab,
    useSetSidebarPanelContent,
    useVisibleSidebarPanelContent,
    useCanCollapse,
    useSetIsMobileHeaderSheetOpen,
    DatadogDurationVital,
    DatadogDurationVitalStopper,
    GET_EVIDENCE_AWARENESS_POPUP_VARIANT,
    IS_ASSISTANT_VIEW_ENABLED,
    IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED,
    DEFAULT_DATE_FORMAT,
    useSessionsListHotkeys,
    useTutorial,
    ProfileSearchSvg,
    HeidiTogetherBoxesSvg,
    AccordionNavV3Button,
    UploadSessionScheduleModal,
    useShowSessionsListAssistantView,
    IS_UPCOMING_SESSIONS_ENABLED,
    useIsCalendarEventTitleEnabled,
    useIsScheduleFacetFilteringEnabled,
    useIsProblemBasedChartingEnabled,
    useIsScheduleRowFacetsEnabled,
    useIsScheduleDateRangeEnabled,
    useSetSessionListAnchorsMounted,
    statusIconColors,
    startDurationVital,
    proseMirrorClassName: styles.ProseMirror ?? '',
    SendAsEmailModal,
    useShowSendAsEmailModal,
    useSetShowSendAsEmailModal,
    simpleMarkdownToHtml,
    printDocument,
    useBlockEditorMarkdown,
    ExportSubmenu,
    useExportFontRequest,
    useSetExportFontRequest,
    IS_HIDE_EMAIL_FUNCTIONALITY_ENABLED,
  },
  sessionApp: {
    useSessionDateDisplay,
    useSessionListVersion,
    useSessionQueryVersion,
    useSetSessionQueryVersion,
    useVisibleSessionStatuses,
    sessionStatusChipConfig,
    statusIconVariants,
    statusChipVariants,
    SESSION_STATUS_DISPLAY_ORDER,
    sessionUrgencyMessages,
    sessionStatusDescription: enumToDescription,
    useNewSession,
    useNewSessionMutationPending,
    useDebouncedNewSession,
    useQueryCombinedSessions,
    usePrefetchSessionById,
    useMutateUpdateSessionStatus,
    useMutateUpdateSyncDocumentById,
    useSessionCopy,
    useMutateDeleteEmptySessions,
    useFormatSessionTime,
    filterAndSortSessions,
    buildSessionStatusOptions,
    isSessionUrgent,
    SessionUrgencyFlag,
    useSetDeleteSessionData,
    compareSessionsByStatus,
    useSessionRecordingStatus,
    useScheduleDate,
    useScheduleDateState,
    useMutateCreateNewSession,
    useGetPageSize,
  },
  analytics: {
    useSessionSearchTracking,
  },
  foreignApp: {
    useIsAmbientCopilotEnabled,
    logCopilotAction,
    COPILOT_PRECHART_REQUEST_TTL_MS,
    useCopilotPrechartRequest,
    useSetCopilotPrechartBatch,
    getCopilotPrechartRequest: () => getDefaultStore().get(copilotPrechartRequestAtom),
    setCopilotPrechartRequest: (request) => {
      getDefaultStore().set(copilotPrechartRequestAtom, request);
    },
    useSetEvidenceSidebarOpen,
    useSessionlessEvidenceAwarenessDevVariantOverride,
    useSessionlessEvidenceAwarenessDevForceShow,
    SessionlessEvidenceAwarenessPopover,
  },
  desktop: {
    useOfflineGating,
    OfflineGatedTooltip,
    useIsOfflineRecordingEnabled,
    useIsDesktopChronicleEnabled,
    useIsReleaseRemoteSyncAsyncWebEnabled,
    useIsInterruptedRemoteSession,
    useIsLiveOfflineRemoteSession,
    useIsLiveRunningRemoteSession,
    useIsPureOfflineRemoteSession,
    useIsRemoteReconnectedSession,
    useQueryInterruptedSessions,
    useTidyUpRecordingLock,
    useChronicleDeviceRecordingInfo,
    useChronicleIsPaused,
    useChronicleLiveOffline,
    useChronicleReconnectedPendingEnd,
    useChronicleSessionId,
    useBulkSyncPhase,
    useBulkSyncUploadProgress,
    useBulkSyncExpectedUploadTotal,
    useBulkSyncCumulativeProcessed,
    useActiveSessionId,
    useOfflineActiveRecordingId,
    useInFlightRecordingSessionId,
    useSetPendingRecordingAction,
    useOfflineCaptureRunning,
    useOfflineLocalSessions,
    useOfflineSyncQueue,
    useOfflinePendingServerDeleteIds,
    useOfflineDismissedSyncedBadgeIds,
    useOfflineNoteFailedDismissedSessionIds,
    useOfflineNoteFailedSessionIds,
    useOfflinePatientLink,
    offlineGatingMessages,
    SyncStatusBadge,
    useOfflineAwareSyncStatus,
    RemoteDeviceSvg,
    isOfflineDeletableStatus,
  },
  patients: {
    useQueryPatientProfiles,
    PatientLinkAvatar,
    SessionListLinkOverlays,
    ConsolidatedSessionLinkAvatar,
    SessionsListFooterLinkControl,
    useMutateCreateAndLinkPatientProfile,
    useMutateLinkSessionsToPatientProfile,
    trackPatientSelected,
  },
};
